import { createResourceFromDescriptor } from './resourceCreation.js';
import { extractResourceTypeHandleFromRole, getIntrinsicInputByRoleName, getIntrinsicInputRoleNames, } from './workflowExtraction.js';
export function getAuthoringSteps(workflow) {
    return workflow.stepsByThreadIndex[0] ?? [];
}
export function setAuthoringSteps(workflow, steps) {
    const nextStepsByThreadIndex = [...workflow.stepsByThreadIndex];
    nextStepsByThreadIndex[0] = steps;
    return {
        ...workflow,
        stepsByThreadIndex: nextStepsByThreadIndex,
    };
}
export function getWorkflowStateEntryByRoleName(workflowState, stepKey) {
    return workflowState[stepKey];
}
export function getWorkflowStateEntry(workflowState, toolStepRoleAddress) {
    const inputEntryByRoleName = getWorkflowStateEntryByRoleName(workflowState, toolStepRoleAddress.stepKey);
    return inputEntryByRoleName?.[toolStepRoleAddress.roleName];
}
export function setWorkflowStateEntry(workflowState, toolStepRoleAddress, entry) {
    const inputEntryByRoleName = getWorkflowStateEntryByRoleName(workflowState, toolStepRoleAddress.stepKey) ?? {};
    return {
        ...workflowState,
        [toolStepRoleAddress.stepKey]: {
            ...inputEntryByRoleName,
            [toolStepRoleAddress.roleName]: entry,
        },
    };
}
export function clearInputBinding(workflowState, toolStepRoleAddress) {
    const inputEntryByRoleName = getWorkflowStateEntryByRoleName(workflowState, toolStepRoleAddress.stepKey);
    if (!inputEntryByRoleName || !(toolStepRoleAddress.roleName in inputEntryByRoleName)) {
        return workflowState;
    }
    const nextInputEntryByRoleName = { ...inputEntryByRoleName };
    delete nextInputEntryByRoleName[toolStepRoleAddress.roleName];
    if (Object.keys(nextInputEntryByRoleName).length === 0) {
        const nextWorkflowState = { ...workflowState };
        delete nextWorkflowState[toolStepRoleAddress.stepKey];
        return nextWorkflowState;
    }
    return {
        ...workflowState,
        [toolStepRoleAddress.stepKey]: nextInputEntryByRoleName,
    };
}
export function bindMaterializedResource(workflowState, target, resource) {
    const mutableMaterializedResource = {
        ...resource,
        workflowStateEntryKind: 'materializedResource',
    };
    return setWorkflowStateEntry(workflowState, target, mutableMaterializedResource);
}
export function bindMaterializedResource2(workflowState, target, materializedResource) {
    return setWorkflowStateEntry(workflowState, target, materializedResource);
}
export function materializeIntrinsicToolStepInputs(args) {
    if (args.toolStep.toolHandle !== args.tool.handle) {
        throw new Error(`ToolStep '${args.toolStep.stepKey}' references '${args.toolStep.toolHandle}', not '${args.tool.handle}'.`);
    }
    const intrinsicInputByRoleName = getIntrinsicInputByRoleName(args.tool);
    let nextWorkflowState = args.workflowState;
    for (const roleName of getIntrinsicInputRoleNames(args.tool)) {
        if (!args.toolStep.roleBindingSpec.inputBindings.includes(roleName)) {
            throw new Error(`Intrinsic input role '${roleName}' is missing from ToolStep '${args.toolStep.stepKey}'.`);
        }
        if (getWorkflowStateEntry(nextWorkflowState, {
            stepKey: args.toolStep.stepKey,
            roleName,
        }) !== undefined) {
            throw new Error(`Intrinsic input role '${roleName}' on ToolStep '${args.toolStep.stepKey}' is already bound.`);
        }
        const role = args.tool.roleSpec.inputRoleByName[roleName];
        if (!role) {
            throw new Error(`Input role '${roleName}' is not declared by Tool '${args.tool.handle}'.`);
        }
        const resourceTypeHandle = extractResourceTypeHandleFromRole(role);
        if (resourceTypeHandle === null) {
            throw new Error(`Intrinsic input role '${roleName}' on Tool '${args.tool.handle}' is not resource-backed.`);
        }
        nextWorkflowState = bindMaterializedResource(nextWorkflowState, {
            stepKey: args.toolStep.stepKey,
            roleName,
        }, createResourceFromDescriptor({
            id: args.generateIdentifier('ResourceId'),
            resourceTypeHandle,
        }, intrinsicInputByRoleName[roleName]));
    }
    return nextWorkflowState;
}
function getInputRole(tool, roleName) {
    return tool.roleSpec.inputRoleByName[roleName];
}
function getOutputRole(tool, roleName) {
    return tool.roleSpec.outputRoleByName[roleName];
}
export function bindInternalInputPotential(workflowState, targetAddress, sourceAddress, context) {
    const sourceToolStep = context.toolStepByKey.get(sourceAddress.stepKey);
    if (!sourceToolStep) {
        throw new Error(`Source tool step not found for '${sourceAddress.stepKey}'.`);
    }
    const targetToolStep = context.toolStepByKey.get(targetAddress.stepKey);
    if (!targetToolStep) {
        throw new Error(`Target tool step not found for '${targetAddress.stepKey}'.`);
    }
    const sourceTool = context.toolMap.get(sourceToolStep.toolHandle);
    if (!sourceTool) {
        throw new Error(`Source tool not found for '${sourceToolStep.toolHandle}'.`);
    }
    const targetTool = context.toolMap.get(targetToolStep.toolHandle);
    if (!targetTool) {
        throw new Error(`Target tool not found for '${targetToolStep.toolHandle}'.`);
    }
    const targetInputRole = getInputRole(targetTool, targetAddress.roleName);
    if (!targetInputRole) {
        throw new Error(`Target input role not found for (${targetAddress.stepKey}, ${targetAddress.roleName}).`);
    }
    const sourceOutputRole = getOutputRole(sourceTool, sourceAddress.roleName);
    const sourceInputRole = getInputRole(sourceTool, sourceAddress.roleName);
    const sourceInputEntryByRoleName = workflowState[sourceAddress.stepKey];
    const sourceInputEntry = sourceInputEntryByRoleName?.[sourceAddress.roleName];
    const sourceRoleType = sourceOutputRole
        ? extractResourceTypeHandleFromRole(sourceOutputRole)
        : sourceInputRole
            ? extractResourceTypeHandleFromRole(sourceInputRole)
            : null;
    const targetRoleType = extractResourceTypeHandleFromRole(targetInputRole);
    if (!sourceOutputRole && sourceInputRole) {
        if (!sourceInputEntry) {
            throw new Error(`Source input role is not bound for (${sourceAddress.stepKey}, ${sourceAddress.roleName}).`); // ATTENTION: not an ontological invariant, but it might help catch some mistakes early on.
        }
    }
    if (!sourceOutputRole && !sourceInputRole) {
        throw new Error(`Source role not found for (${sourceAddress.stepKey}, ${sourceAddress.roleName}).`);
    }
    if (!sourceRoleType || !targetRoleType || sourceRoleType !== targetRoleType) {
        throw new Error(`Reference type mismatch between source (${sourceAddress.stepKey}, ${sourceAddress.roleName}) and target (${targetAddress.stepKey}, ${targetAddress.roleName}).`);
    }
    return setWorkflowStateEntry(workflowState, targetAddress, {
        workflowStateEntryKind: 'internalInputPotential',
        toolStepRoleAddress: {
            stepKey: sourceAddress.stepKey,
            roleName: sourceAddress.roleName,
        },
    });
}
export function bindExternalInputPotential(workflowState, target) {
    const mutableExternalInputPotential = {
        workflowStateEntryKind: 'externalInputPotential',
    };
    return setWorkflowStateEntry(workflowState, target, mutableExternalInputPotential);
}
